import { 
  Button, 
  TextField, 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  Alert, 
  useTheme, 
  Tooltip, 
  IconButton,
  FormControlLabel,
  Checkbox,
  Switch
} from "@mui/material";
import { useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ModalMAIQ from "app/components/ModalMAIQ";
import { Scope } from "../../types";
import { useScope } from "app/context/ScopeContext";

interface AddScopeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddScopeModal = ({ open, onClose, onSuccess }: AddScopeModalProps) => {
  const { addScope } = useScope();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const theme = useTheme();
  const [scope, setScope] = useState<Partial<Scope>>({
    name: '',
    description: '',
    scope: '',
    status: true,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setScope(prev => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScope(prev => ({ ...prev, status: e.target.checked }));
  };

  const validateForm = () => {
    if (!scope.name?.trim()) {
      setError("Name is required");
      return false;
    }
    if (!scope.scope?.trim()) {
      setError("Scope identifier is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError("");
    
    try {
      await addScope(scope);
      onSuccess();
      onClose();
      // Reset form
      setScope({
        name: '',
        description: '',
        scope: '',
        status: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add scope');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalMAIQ 
      open={open} 
      onClose={onClose}
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VpnKeyIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Add New Scope
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Name *
            </Typography>
            <TextField
              fullWidth
              name="name"
              value={scope.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter scope name"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Scope Identifier *
            </Typography>
            <TextField
              fullWidth
              name="scope"
              value={scope.scope}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter scope identifier (e.g., 'read:users')"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Description
            </Typography>
            <TextField
              fullWidth
              name="description"
              value={scope.description}
              onChange={handleChange}
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              multiline
              rows={3}
              placeholder="Enter scope description"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={scope.status}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Active
                </Typography>
              }
            />
          </Box>
        </Box>

        {/* Footer with actions - fixed */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              color: 'text.primary',
              borderColor: 'divider',
              py: 0.75
            }}
          >
            Cancel
          </Button>
          <Tooltip title="Create a new scope">
            <Button
              variant="contained"
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600, 
                bgcolor: theme.palette.primary.main,
                color: 'white',
                py: 0.75,
                px: 3,
                '&:hover': {
                  bgcolor: '#0a1f3a',
                }
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Scope'}
            </Button>
          </Tooltip>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddScopeModal; 